import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TYPOGRAPHY_SIZE } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../../../theming/useTheme';
import { ChevronIcon, Directions } from '../ChevronIcon';
import { HeaderLinksDataProps } from '../Header';
import { DropDownMenu } from '../DropDownMenu';
import { Span, semanticCSS } from '../../../../../typography/semanticTags';
import { SemanticTagThemeProps, getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';

const componentConfigThemeKey = 'moreLinks';

export interface MoreLinksProps {
  moreLinksText: string
  links: HeaderLinksDataProps[]
  pagePath: string
}

export interface MoreLinksThemeProps extends SemanticTagThemeProps {
  titleColor: string
  menuBackground: string
  linkColor: string
  hoverColor: string
}

export type MoreLinksComponentProps = MoreLinksThemeProps & MoreLinksProps;

const getStyle = (theme: MoreLinksThemeProps) => {
  const { titleColor, menuBackground } = theme;

  return StyleSheet.create({
    wrapper: {
      position: 'relative',
      height: '100%',
      display: 'flex',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
    },
    buttonTitle: {
      color: titleColor,
      marginRight: '9px',
    },
    menuBody: {
      listStyle: 'none',
      width: '100%',
      position: 'absolute',
      backgroundColor: menuBackground,
    },
  });
};

export const MoreLinksComponent: React.FunctionComponent<MoreLinksComponentProps> = props => {
  const { moreLinksText, links, pagePath, fontSizesStyles } = props;
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const toggleMenu = React.useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);


  const style = getStyle(props);
  const titleStyle = getStyleBySize(fontSizesStyles, { fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL, fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL, fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL });
  const chevronDirection = isMenuOpen ? Directions.UP : Directions.DOWN;

  return (
    <div className={css(style.wrapper)}>
      <button type="button" className={css(style.button)} onClick={toggleMenu}>
        <Span styles={semanticCSS(titleStyle, style.buttonTitle)}>{moreLinksText}</Span>
        <ChevronIcon direction={chevronDirection} />
      </button>
      {
        isMenuOpen && <DropDownMenu configThemeKey="moreLinks" menuLinks={links} highlightActive activeEndpoint={pagePath} />
      }
    </div>
  );
};


const mapThemeToProps = (theme: MoreLinksThemeProps) => (
  {
    titleColor: theme.titleColor,
    menuBackground: theme.menuBackground,
    linkColor: theme.linkColor,
    hoverColor: theme.hoverColor,
    fontSizesStyles: theme.fontSizesStyles,
  }
);

export const MoreLinks = (props: MoreLinksProps) => {
  const themeContext = useTheme(mapThemeToProps, componentConfigThemeKey);
  return <MoreLinksComponent {...themeContext} {...props} />;
};
